import * as React from 'react';
import styled from 'styled-components';
import { WidthProps, PaddingProps, padding } from 'styled-system';

import { themeGet, is, isNot } from '../../theme/utils';
import { Icon, IconNames, IconColors } from '../icons';
import { Button, ButtonProps } from './Button';

export const IconStyled = styled(Icon)`
  flex: 0 0 ${(props) => props.width};
`;

interface ButtonWithIconStyledProps extends PaddingProps {
  reversed?: boolean;
  iconOnly: boolean;
  align?: 'right' | 'left' | 'center';
}

export const ButtonWithIconStyled = styled(Button)<ButtonWithIconStyledProps & { iconMargin: number } & WidthProps>`
  ${padding};
  display: flex;
  ${is('reversed')`
    flex-direction: row-reverse;
  `};
  justify-content: ${({ align }) =>
    align === 'right' ? 'flex-end' : align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'space-between'};
  ${isNot('iconOnly')`
    ${IconStyled} {
      ${isNot('reversed')`
        margin-right: ${({ iconMargin }: any) => themeGet.space(iconMargin)};
      `};
      ${is('reversed')`
        margin-left: ${({ iconMargin }: any) => themeGet.space(iconMargin)};
      `};
    };
  `};
`;

ButtonWithIconStyled.defaultProps = {
  padding: `0 ${themeGet.space(3)}`,
};

const PlainTextLabel = styled.span`
  flex: 1 1 auto;
`;

export interface ButtonWithIconProps
  extends Partial<ButtonWithIconStyledProps>,
    ButtonProps,
    React.HTMLAttributes<HTMLButtonElement> {
  iconName: IconNames;
  iconColor?: IconColors;
  iconSize?: string;
  iconMargin?: number;
}

export const ButtonWithIcon: React.FC<ButtonWithIconProps & WidthProps & { ref?: React.Ref<HTMLButtonElement> }> =
  React.forwardRef(
    (
      {
        iconName,
        children,
        iconColor = 'inherit',
        iconSize = '16px',
        iconMargin = 2,
        reversed = false,
        className,
        padding,
        ...buttonProps
      },
      ref,
    ) => {
      const labelIsPlainText = children && (typeof children === 'string' || children instanceof String);
      return (
        <ButtonWithIconStyled
          className={className}
          padding={padding}
          reversed={reversed}
          iconMargin={iconMargin}
          iconOnly={!children}
          ref={ref}
          {...buttonProps}
        >
          <IconStyled color={iconColor} name={iconName} width={iconSize} />
          {labelIsPlainText ? <PlainTextLabel>{children}</PlainTextLabel> : <>{children}</>}
        </ButtonWithIconStyled>
      );
    },
  );

ButtonWithIcon.displayName = 'ButtonWithIcon';
